import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { AlertIcon, Icon, SuccessIcon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { invalidate, useQuery, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { moment } from "@msuite/utils/date";
import { guardString } from "@msuite/utils/strings";
import type { Vehicle } from "@msuite/utils/types";
import React from "react";
import { Can } from "@/components/citadel";
import { client } from "@/hono";
import { useCitadel } from "@/hooks/use-citadel";
import { ConnectVehicleModal } from "@/modals/vehicles/connect-vehicle-modal";

/** Props Interface */
interface WebfleetIntegrationProps {
	vehicleId: string;
	vehicleIntegrations?: Vehicle["_integrations"];
}

export function WebfleetIntegration({ vehicleId, vehicleIntegrations }: WebfleetIntegrationProps) {
	/** Hooks */
	const { t } = useTranslation();
	const connectVehicleModal = useDisclosure();
	const asyncModal = useAsyncModal();
	const qc = useQueryClient();
	const { citadel } = useCitadel();

	/** Computed */
	// Reading a lorry's pairing is part of reading the lorry, so the route behind this query is gated
	// on `read Vehicle`. Rules still loading leave it enabled and let the server answer.
	const canRead = citadel ? citadel.can("read", "Vehicle") : true;

	/** Query */
	const { data: vehicleIntegration, refetch } = useQuery({
		queryKey: ["vehicle", "integrations", guardString(vehicleId)],
		queryFn: async () => {
			if (!vehicleId) return vehicleIntegrations?.at(0);
			const response = await client.vehicles[":vehicleId"].integrations.$get({
				param: { vehicleId },
				query: {
					startDate: moment().startOf("year").db(),
					endDate: moment().endOf("year").db(),
				},
			});
			const data = await responseHandler(response);
			if (!data.integrations) return null;
			return data.integrations;
		},
		enabled: (Boolean(vehicleId) || !vehicleIntegrations?.length) && canRead,
	});

	const hasVehicleIntegration = Boolean(vehicleIntegration);

	/** Functions */
	async function handleOnSelectWebfleetObject(identifier: string) {
		connectVehicleModal.onClose();
		await client.vehicles[":vehicleId"].integration.$post({
			param: { vehicleId },
			json: { identifier },
		});
		await handleOnInvalidate();
	}

	async function handleOnDisconnect() {
		if (!vehicleIntegration) return;
		const isConfirmed = await asyncModal.confirm();
		if (!isConfirmed) return;
		await client.vehicles[":vehicleId"].integration[":integrationId"].$delete({
			param: { vehicleId, integrationId: vehicleIntegration.id },
		});
		await handleOnInvalidate();
	}

	async function handleOnInvalidate() {
		await qc.invalidateQueries({
			predicate: invalidate(vehicleId),
		});
		await refetch();
	}

	/** Render */
	return (
		<React.Fragment>
			<div className="h-stack justify-between w-full">
				{vehicleIntegration ? <SuccessIcon /> : <AlertIcon />}
				<p>Webfleet</p>
				<div className="flex-1" />
				{/* Pairing and unpairing change the lorry, which is the rule both routes check. */}
				<Can
					action="update"
					subject="Vehicle"
				>
					{!hasVehicleIntegration ? (
						<Button
							variant="white"
							size="xxs"
							icon={<Icon name="Zap" />}
							onClick={connectVehicleModal.onOpen}
						>
							{t("integrations.connect")}
						</Button>
					) : (
						<Button
							icon={<Icon name="Unlink" />}
							size="xxs"
							variant="danger-ghost"
							onClick={handleOnDisconnect}
							isLoading={asyncModal.isOpen}
						>
							{t("integrations.disconnect")}
						</Button>
					)}
				</Can>
			</div>
			{connectVehicleModal.isOpen && (
				<ConnectVehicleModal
					isOpen={connectVehicleModal.isOpen}
					onClose={connectVehicleModal.onClose}
					onSelect={handleOnSelectWebfleetObject}
				/>
			)}
			{asyncModal.isOpen && (
				<ConfirmModal
					controller={asyncModal}
					message={t("integrations.confirm_message_disconnect_integration")}
				/>
			)}
		</React.Fragment>
	);
}
